import React, { useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Phone, Mail, Loader2, Check, MapPin } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { CONTACT } from "@/data/site";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";
import Seo from "@/components/Seo";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Bitte fülle Name, E-Mail und Nachricht aus.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      setDone(true);
      toast.success("Nachricht gesendet!", { description: "Wir melden uns in Kürze bei dir." });
    } catch {
      toast.error("Senden fehlgeschlagen", { description: "Bitte versuche es erneut." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-ink pt-[70px] md:pt-[84px]">
      <Seo title="Kontakt" description="Fragen zu Produkten, Bestellungen oder Team-Angeboten? Kontaktiere STARSOX per Formular, Telefon oder Instagram." path="/contact" />
      <section className="container-x pt-16 md:pt-24 pb-4">
        <p className="overline mb-4">Kontakt</p>
        <MaskedLines start className="font-heading uppercase leading-[0.9]" lineClassName="text-6xl md:text-8xl" lines={["Sag Hallo"]} />
      </section>

      <section className="container-x py-12 md:py-20 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        {/* Form */}
        <Reveal>
          <div className="card-hair p-8 md:p-10">
            {done ? (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12" data-testid="contact-success">
                <div className="h-16 w-16 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center mx-auto"><Check className="w-8 h-8 text-gold" /></div>
                <h3 className="font-heading text-3xl uppercase mt-6">Danke!</h3>
                <p className="text-white/60 mt-3">Deine Nachricht ist bei uns eingegangen. Wir melden uns so schnell wie möglich.</p>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                <div>
                  <label className="field-label" htmlFor="c-name">Name*</label>
                  <input id="c-name" data-testid="contact-name" className="field" value={form.name} onChange={set("name")} placeholder="Dein Name" required />
                </div>
                <div>
                  <label className="field-label" htmlFor="c-email">E-Mail*</label>
                  <input id="c-email" type="email" data-testid="contact-email" className="field" value={form.email} onChange={set("email")} placeholder="name@example.de" required />
                </div>
                <div>
                  <label className="field-label" htmlFor="c-subject">Betreff</label>
                  <input id="c-subject" data-testid="contact-subject" className="field" value={form.subject} onChange={set("subject")} placeholder="Worum geht's?" />
                </div>
                <div>
                  <label className="field-label" htmlFor="c-message">Nachricht*</label>
                  <textarea id="c-message" rows={5} data-testid="contact-message" className="field resize-none" value={form.message} onChange={set("message")} placeholder="Deine Nachricht …" required />
                </div>
                <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-primary w-full disabled:opacity-60">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Nachricht senden"}
                </button>
              </form>
            )}
          </div>
        </Reveal>

        {/* Info */}
        <Reveal delay={0.1}>
          <div className="space-y-6">
            <h2 className="font-heading text-3xl md:text-4xl uppercase">Direkter Draht</h2>
            <p className="text-white/50 max-w-md">Fragen zu Produkten, Bestellungen oder Team-Angeboten? Erreiche uns über folgende Kanäle:</p>

            <div className="space-y-3">
              <a href={CONTACT.instagramUrl} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="flex items-center gap-4 card-hair p-5 hover:border-gold/50 transition-colors">
                <span className="h-11 w-11 rounded-full bg-white/5 flex items-center justify-center"><Instagram className="w-5 h-5 text-gold" /></span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">Instagram</p>
                  <p className="text-white">{CONTACT.instagram}</p>
                </div>
              </a>
              <a href={`tel:${CONTACT.phone}`} data-testid="contact-phone" className="flex items-center gap-4 card-hair p-5 hover:border-gold/50 transition-colors">
                <span className="h-11 w-11 rounded-full bg-white/5 flex items-center justify-center"><Phone className="w-5 h-5 text-gold" /></span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">Telefon</p>
                  <p className="text-white">{CONTACT.phoneDisplay}</p>
                </div>
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex items-center gap-4 card-hair p-5 hover:border-gold/50 transition-colors">
                <span className="h-11 w-11 rounded-full bg-white/5 flex items-center justify-center"><Mail className="w-5 h-5 text-gold" /></span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">E-Mail</p>
                  <p className="text-white">{CONTACT.email}</p>
                </div>
              </a>
              <div className="flex items-center gap-4 card-hair p-5">
                <span className="h-11 w-11 rounded-full bg-white/5 flex items-center justify-center"><MapPin className="w-5 h-5 text-gold" /></span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">Versand</p>
                  <p className="text-white">Deutschlandweit · starsox.de</p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
